import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface AnalyticsData {
  period: number;
  totalShown: number;
  engaged: number;
  converted: number;
  engagementRate: number;
  conversionRate: number;
  byOutcome: { outcome: string; count: number }[];
}

export default function ProactiveAnalytics() {
  const api = useApi();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.get(`ProactiveEngagement/analytics?days=${days}`);
      const json = await res.json();
      setData(json);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin, days]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <div className="text-center py-12 text-gray-400">Loading analytics...</div>;
  }

  if (!data || data.totalShown === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
        <i className="fa-solid fa-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-gray-500 dark:text-gray-400 mb-2">No engagement data yet</p>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          Create and enable proactive rules to start seeing analytics here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Period:</span>
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              days === d
                ? "bg-cornflower-blue-500 text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
            }`}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-4 gap-4">
        <MetricCard label="Messages Shown" value={data.totalShown} icon="fa-eye" color="blue" />
        <MetricCard label="Engaged" value={data.engaged} icon="fa-comment-dots" color="green" />
        <MetricCard label="Converted" value={data.converted} icon="fa-check-circle" color="purple" />
        <MetricCard
          label="Engagement Rate"
          value={`${data.engagementRate}%`}
          icon="fa-chart-line"
          color="amber"
        />
      </div>

      {/* Outcome breakdown */}
      {data.byOutcome.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Outcome Breakdown</h3>
          <div className="space-y-2">
            {data.byOutcome.map((item) => {
              const pct = data.totalShown > 0 ? (item.count / data.totalShown) * 100 : 0;
              return (
                <div key={item.outcome} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-20 capitalize">{item.outcome}</span>
                  <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className="bg-cornflower-blue-500 rounded-full h-2 transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right">
                    {item.count} ({pct.toFixed(1)}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: string;
  color: string;
}) {
  const colorMap: Record<string, string> = {
    blue: "text-cornflower-blue-500 bg-cornflower-blue-50 dark:bg-cornflower-blue-900/20",
    green: "text-green-500 bg-green-50 dark:bg-green-900/20",
    purple: "text-purple-500 bg-purple-50 dark:bg-purple-900/20",
    amber: "text-amber-500 bg-amber-50 dark:bg-amber-900/20",
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${colorMap[color] ?? colorMap.blue}`}>
          <i className={`fa-solid ${icon} text-xs`} />
        </div>
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
